import React, { useMemo, useCallback, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { classNames, Icon, iconProps } from '@zambezi/sdk/common'
import { getFlattenedSuggestions } from './utils'

function selectMobile (
  {
    isError,
    isDisabled,
    onBlur,
    onChange,
    onFocus,
    onSuggestionSelected,
    value,
    defaultValue,
    suggestions,
    withGrouping,
    getGroupChildren,
    getSuggestionValue,
    renderGroupLabel,
    inputProps,
    iconProps,
    disabledItemsIndices
  },
  ref
) {
  const flattenedSuggestions = useMemo(
    () =>
      getFlattenedSuggestions(
        suggestions,
        withGrouping,
        getGroupChildren,
        getSuggestionValue
      ),
    [suggestions, withGrouping, getGroupChildren, getSuggestionValue]
  )
  const handleChange = useCallback(
    event => {
      const { value } = event.target
      const method = 'click'
      onSuggestionSelected(event, {
        suggestion: value,
        suggestionIndex: flattenedSuggestions.indexOf(value),
        method
      })
      onChange(event, { value, method })
    },
    [onChange, onSuggestionSelected, flattenedSuggestions]
  )
  const currentValue = value === undefined ? defaultValue : value
  const hasDisabledItems = !!disabledItemsIndices.length
  const selectProps = {
    ...inputProps,
    className: classNames('zb-select-is-mobile', {
      'zb-select-mobile-is-error': isError,
      'zb-select-mobile-is-disabled': isDisabled
    }),
    'aria-invalid': !!isError,
    disabled: isDisabled,
    onChange: handleChange,
    onBlur,
    onFocus
  }
  if (value !== undefined) {
    selectProps.value = value
  } else if (defaultValue) {
    selectProps.defaultValue = defaultValue
  }

  return (
    <div className='zb-select-is-mobile-wrapper' ref={ref}>
      <Icon
        name='core-select-collapsed'
        aria-hidden='true'
        title=''
        iconProps={iconProps}
      />
      <select {...selectProps}>
        {currentValue && flattenedSuggestions.indexOf(currentValue) === -1 && (
          <option className='zb-dropdown-list-item'>{currentValue}</option>
        )}
        {
          suggestions.reduce(withGrouping ? renderGroup : renderOption, {
            items: [],
            offset: 0
          }).items
        }
      </select>
    </div>
  )

  function renderOption ({ items, offset }, value, index) {
    const label = getSuggestionValue(value)
    const isDisabled =
      hasDisabledItems && disabledItemsIndices.indexOf(offset + index) !== -1
    return {
      items: items.concat(
        <option
          value={label}
          key={index}
          className='zb-dropdown-list-item'
          disabled={isDisabled}
        >
          {label}
        </option>
      ),
      offset
    }
  }

  function renderGroup ({ items, offset }, value, index) {
    const children = getGroupChildren(value)
    const isDisabled =
      hasDisabledItems &&
      children.every(
        (_, index) => disabledItemsIndices.indexOf(offset + index) !== -1
      )
    return {
      items: items.concat(
        <optgroup
          key={index}
          label={renderGroupLabel(value)}
          disabled={isDisabled}
        >
          {
            children.reduce(renderOption, {
              items: [],
              offset
            }).items
          }
        </optgroup>
      ),
      offset: offset + children.length
    }
  }
}

const SelectMobile = forwardRef(selectMobile)
SelectMobile.displayName = 'SelectMobile'
SelectMobile.propTypes = {
  suggestions: PropTypes.array.isRequired,
  disabledItemsIndices: PropTypes.array.isRequired,
  getSuggestionValue: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  onSuggestionSelected: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  iconProps
}

export default SelectMobile
